import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';

declare global {
  interface Window {
    grecaptcha: any;
    onRecaptchaLoad: () => void;
  }
}

interface RecaptchaV2Props {
  siteKey: string;
  onVerify: (token: string) => void;
  onExpire?: () => void;
  invisible?: boolean;
}

export interface RecaptchaV2Handle {
  execute: () => void;
  reset: () => void;
}

const RecaptchaV2 = forwardRef<RecaptchaV2Handle, RecaptchaV2Props>(({ 
  siteKey, 
  onVerify, 
  onExpire,
  invisible = true 
}, ref) => {
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  useEffect(() => {
    // Check if grecaptcha is already loaded
    if (window.grecaptcha && window.grecaptcha.render) {
      setIsScriptLoaded(true);
      return;
    }

    // Define the callback function
    window.onRecaptchaLoad = () => {
      setIsScriptLoaded(true);
    };

    // Load the reCAPTCHA script
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove the script if component unmounts
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    if (isScriptLoaded && recaptchaRef.current && widgetIdRef.current === null) {
      // Render the reCAPTCHA widget
      try {
        widgetIdRef.current = window.grecaptcha.render(recaptchaRef.current, {
          sitekey: siteKey,
          size: invisible ? 'invisible' : 'normal',
          callback: (token: string) => {
            onVerify(token);
          },
          'expired-callback': () => {
            if (onExpire) {
              onExpire();
            }
          }
        });
      } catch (error) {
        console.error('Error rendering reCAPTCHA:', error);
      }
    }
  }, [isScriptLoaded, siteKey, onVerify, onExpire, invisible]);

  const execute = () => {
    if (widgetIdRef.current !== null && window.grecaptcha) {
      window.grecaptcha.execute(widgetIdRef.current);
    }
  };

  const reset = () => {
    if (widgetIdRef.current !== null && window.grecaptcha) {
      window.grecaptcha.reset(widgetIdRef.current);
    }
  };

  // Expose execute and reset methods via ref
  useImperativeHandle(ref, () => ({
    execute,
    reset
  }), []);

  return <div ref={recaptchaRef} />;
});

RecaptchaV2.displayName = 'RecaptchaV2';

export default RecaptchaV2;